import { DateTime } from 'luxon';

import { Campaign, LineItem, Plan } from '@/common/models';
import PackageGroup from '@/common/models/package-groups/PackageGroup';
import ErrorHandler from '@/ErrorHandler';
import API from '@/org-app/api';
import rootStore from '@/store';
import {
  createApiAction,
  createDeleteAction,
  createGetAllAction,
  createGetOneAction,
  createPostAction,
  createPutAction,
} from '@/utils/ActionsUtils';
import i18n from '@/vueI18n';

import * as types from './mutation-types';

const actions = {
  getPlans: createGetAllAction(API.instance.getPlans, types.UPDATE_PLANS_LIST, 'messages.failedToGetPlans', false),
  getPlan: createGetOneAction(API.instance.getPlan, types.UPDATE_PLAN, 'messages.failedToGetPlan', true),
  createPlans: createPostAction(API.instance.postPlan, types.SAVE_PLAN, 'messages.failedToSavePlan'),
  updatePlan: createPutAction(API.instance.putPlan, types.UPDATE_PLAN, 'messages.failedToUpdatePlan'),
  deletePlan: createDeleteAction(API.instance.deletePlan, types.DELETE_PLAN, 'messages.failedToDeletePlan'),
  getPlanBreakdownData: createApiAction(API.instance.getPlanBreakdownData, null, 'messages.failedToGetBreakdownData'),

  async createNewCampaignFromPlan(_, { plan }) {
    const today = DateTime.fromISO(DateTime.local(), { zone: 'utc' }).toLocal().toLocaleString(DateTime.DATETIME_MED);
    const organizationCampaignCustomFields = rootStore.getters['orgApp/organizationCampaignCustomFields'];

    // Campaign
    const newCampaign = Campaign.fromObject({
      name: `${plan.name} - ${today}`,
      advertiserID: plan.advertiserID,
      accountID: plan.accountID,
      planID: plan.id,
      campaignProperties: plan.planProperties.filter((x) =>
        organizationCampaignCustomFields.find((y) => x.key === y.key),
      ),
    });
    const [campaign] = await rootStore.dispatch('orgApp/createCampaigns', { instances: [newCampaign] });

    const childrenPlan = plan?.children;
    const isUsingBudgetBreakdown = childrenPlan?.length > 0;
    const lineItemsToCreate = isUsingBudgetBreakdown ? childrenPlan : [plan];

    for await (const lineItem of lineItemsToCreate) {
      const entity = isUsingBudgetBreakdown ? Plan.fromApi(lineItem) : plan;
      const newLineItemEntity = await rootStore.dispatch('orgApp/convertPlanEntityToLineItemEntity', {
        plan,
        entity,
        isUsingBudgetBreakdown,
        campaign,
        lineItem,
      });
      const newLineItem = LineItem.fromObject(newLineItemEntity);
      await rootStore.dispatch('orgApp/saveLineItem', { lineItem: newLineItem, showLoader: true });
    }

    // Automatically create the shared campaign password when a shared plan is saved as a campaign
    if (campaign?.planID) {
      await rootStore.dispatch('orgApp/updateSharedCampaignPassword', { campaign });
    }

    return campaign;
  },

  sharePlan: createPutAction(API.instance.sharePlan, types.SHARE_PLAN, 'messages.failedToSharePlan'),
  getPublicSharedPlan: createPostAction(
    API.instance.getPublicSharedPlan,
    types.GET_PUBLIC_SHARED_PLAN,
    'messages.failedToGetSharedPlan',
  ),

  getPlanApprovals: createApiAction(API.instance.getPlanApprovals, null, 'messages.failedToGetPlanApprovals'),
  sendFeedback: createPostAction(
    API.instance.postPlanApprovals,
    types.SEND_FEEDBACK,
    'messages.failedToSendYourFeedback',
  ),

  async savePlanPackageGroup({ commit }, { plan }) {
    let { packageGroupDealsIDs, packageGroupID } = plan;
    if (packageGroupDealsIDs == null || packageGroupDealsIDs.length === 0) {
      return; // No package group to create/update;
    }

    const getValidGroupDealsIDsSet = (deals) => {
      if (deals instanceof Object && !Array.isArray(deals)) {
        return Object.keys(deals).map((x) => parseInt(x, 10));
      }
      return deals;
    };

    // plan is being duplicated, we need to create a new package group and not link/change the existing one
    if (plan.fromPlanID) {
      packageGroupID = null;
      packageGroupDealsIDs = getValidGroupDealsIDsSet(packageGroupDealsIDs);
    }

    // If Plan has no PackageGroup, create one and link it to the Plan
    if (packageGroupID == null) {
      const packageGroup = new PackageGroup(new Set(packageGroupDealsIDs));
      // 1 - Create the package group
      const result = await rootStore.dispatch('orgApp/createPackageGroup', { instances: packageGroup });
      packageGroupID = result.id;

      // 2 - Assign the created package group ID to the Plan
      await rootStore.dispatch('updatePlanPackageGroupID', {
        plan,
        value: packageGroupID,
      });
    } else {
      // Else, update the existing Package Group with the new deals
      packageGroupDealsIDs = getValidGroupDealsIDsSet(packageGroupDealsIDs);
      const updatedPackageGroup = new PackageGroup(new Set(packageGroupDealsIDs));
      updatedPackageGroup.id = plan.packageGroupID;
      await rootStore.dispatch('orgApp/updatePackageGroup', { instance: updatedPackageGroup });
    }
  },

  async createNewPlan({ commit }, params) {
    const plan = Plan.fromObject(params);
    commit(types.CREATE_PLAN, plan);
    return plan;
  },

  async getPlanExport(_, { planId, format = 'XLSX', timeZone = 'UTC', cancelTokenSource = null }) {
    return await API.instance
      .getPlanExport({
        planId,
        format,
        timeZone,
        cancelTokenSource,
      })
      .catch((err) => {
        const msg = i18n.global.t('messages.exportingPlanFailed');
        ErrorHandler.showError(msg, err.message, err.status);
        throw err;
      });
  },
  postTTDPlan: createApiAction(API.instance.postTTDPlan, null, 'messages.failedToPushToTTD'),
  extendPublicPlan: createApiAction(API.instance.extendPublicPlan, null, 'messages.failedToExtendPublicPlan'),
};

export default actions;
